/**
 * Three.js PointsMaterial sizeAttenuation only scales gl_PointSize for
 * perspective cameras. Under orthographic, world-unit sizes (0.1–0.4) become
 * sub-pixel and fire/rain/snow/stars disappear. Patch the points shader to
 * multiply by camera.zoom when not perspective.
 */

function patchPointsVertexShader(shader, material) {
  if (!shader?.vertexShader) return;

  const zoom = material?.userData?.orthoZoom ?? 1;

  if (shader.vertexShader.includes('uOrthoZoom')) {
    if (shader.uniforms?.uOrthoZoom) {
      shader.uniforms.uOrthoZoom.value = zoom;
    }
    if (material?.userData) {
      material.userData.orthoPointsShader = shader;
    }
    return;
  }

  shader.uniforms.uOrthoZoom = { value: zoom };

  if (!shader.vertexShader.includes('uniform float uOrthoZoom')) {
    shader.vertexShader = shader.vertexShader.replace(
      '#include <common>',
      '#include <common>\nuniform float uOrthoZoom;'
    );
  }

  // Three r15x–r18x points shader attenuation branch
  if (
    shader.vertexShader.includes(
      'if ( isPerspective ) gl_PointSize *= ( scale / - mvPosition.z );'
    )
  ) {
    shader.vertexShader = shader.vertexShader.replace(
      'if ( isPerspective ) gl_PointSize *= ( scale / - mvPosition.z );',
      `if ( isPerspective ) gl_PointSize *= ( scale / - mvPosition.z );
       else gl_PointSize *= max( uOrthoZoom, 0.0001 );`
    );
    return;
  }

  // Fallback: after assigning gl_PointSize = size;
  if (shader.vertexShader.includes('gl_PointSize = size;')) {
    shader.vertexShader = shader.vertexShader.replace(
      'gl_PointSize = size;',
      `gl_PointSize = size;
       if ( ! isPerspectiveMatrix( projectionMatrix ) ) gl_PointSize *= max( uOrthoZoom, 0.0001 );`
    );
  }
}

/**
 * Ensure a PointsMaterial scales correctly under orthographic cameras.
 * Safe to call multiple times.
 */
export function ensurePointsMaterialOrthoSupport(material) {
  if (!material?.isPointsMaterial) return material;
  if (material.userData?.__orthoPointsPatched) return material;

  material.userData = material.userData || {};
  material.userData.__orthoPointsPatched = true;
  if (material.userData.authoredSize == null) {
    material.userData.authoredSize = material.size;
  }
  if (material.userData.orthoZoom == null) {
    material.userData.orthoZoom = 1;
  }

  const previousCompile = material.onBeforeCompile;
  material.onBeforeCompile = (shader, renderer) => {
    if (typeof previousCompile === 'function') {
      previousCompile(shader, renderer);
    }
    patchPointsVertexShader(shader, material);
    material.userData.orthoPointsShader = shader;
  };
  // Force recompile so the patch applies even if the material was already used.
  material.needsUpdate = true;
  return material;
}

function setOrthoZoomUniform(material, zoom) {
  if (!material) return;
  material.userData = material.userData || {};
  material.userData.orthoZoom = zoom;
  const shader = material.userData.orthoPointsShader;
  if (shader?.uniforms?.uOrthoZoom) {
    shader.uniforms.uOrthoZoom.value = zoom;
  }
}

/**
 * Convert authored world-unit point size → screen pixels under ortho.
 * Design viewport: top/bottom ≈ ±height/2 → factor ≈ zoom.
 * Camera assets: top/bottom from size property → factor ≈ height / worldHeight.
 */
export function getOrthoPointsPixelScale(camera, viewportHeight) {
  if (!camera?.isOrthographicCamera) return 1;
  const zoom = Math.max(Number(camera.zoom) || 1, 0.0001);
  const top = Number(camera.top);
  const bottom = Number(camera.bottom);
  const worldHeight =
    Number.isFinite(top) && Number.isFinite(bottom)
      ? Math.abs(top - bottom) / zoom
      : 0;
  const h = Math.max(Number(viewportHeight) || 0, 1);
  if (worldHeight > 1e-8) {
    return Math.max(h / worldHeight, 0.0001);
  }
  return zoom;
}

/**
 * Patch all PointsMaterials in the scene and sync ortho scale uniforms.
 * Call on camera switch, preview look-through, and ortho zoom/frustum changes.
 *
 * @param {THREE.Scene} scene
 * @param {THREE.Camera} camera
 * @param {number} [viewportHeight] - CSS/drawing buffer height in pixels
 */
export function syncParticlePointSizesForCamera(
  scene,
  camera,
  viewportHeight
) {
  if (!scene || !camera) return;

  const scale = camera.isOrthographicCamera
    ? getOrthoPointsPixelScale(camera, viewportHeight)
    : 1;

  scene.traverse((obj) => {
    if (!obj?.isPoints) return;
    const materials = Array.isArray(obj.material)
      ? obj.material
      : [obj.material];
    for (const mat of materials) {
      if (!mat?.isPointsMaterial) continue;
      ensurePointsMaterialOrthoSupport(mat);
      setOrthoZoomUniform(mat, scale);
    }
  });
}

/** Mark animate-loop sync dirty so the next frame re-applies ortho scales. */
export function markParticleOrthoSyncDirty() {
  if (typeof window !== 'undefined') {
    window.__particleOrthoSyncDirty = true;
  }
}

/**
 * Re-apply ortho point-size scaling using the active viewport camera.
 * Call after particle create/update (materials may be replaced or needsUpdate).
 */
export function resyncSceneParticlePoints(scene) {
  if (!scene) return;
  markParticleOrthoSyncDirty();

  const camera =
    (typeof window !== 'undefined' &&
      (window.currentCamera || window.camera)) ||
    null;
  if (!camera) return;

  const canvas =
    (typeof window !== 'undefined' && window.renderer?.domElement) ||
    (typeof document !== 'undefined' && document.querySelector('canvas')) ||
    null;
  const h =
    canvas?.clientHeight ||
    canvas?.height ||
    (typeof window !== 'undefined' ? window.innerHeight : 1) ||
    1;

  syncParticlePointSizesForCamera(scene, camera, h);
}
